import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { candidates, onboarding } from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase } from 'lucide-react';

const CandidateProfile = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        skills: '', experience: '', location: '', preferred_sectors: [], availability: ''
    });

    useEffect(() => {
        loadProfile();
        // Onboarding funnel tracking (admin-only) — fire-and-forget. This
        // page is optional and dashboard-only, reported separately as
        // "Profile edits", not chained into the main funnel.
        onboarding.track('profile_edit_started');
    }, []);

    const loadProfile = async () => {
        try {
            const res = await candidates.getProfile();
            if (res.data) {
                setFormData({
                    ...res.data,
                    skills: res.data.skills?.join(', ') || '',
                    preferred_sectors: res.data.preferred_sectors || []
                });
            }
        } catch (error) {
            console.error('Failed to load profile');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = {
                ...formData,
                skills: formData.skills.split(',').map(s => s.trim()).filter(s => s)
            };
            await candidates.updateProfile(data);
            onboarding.track('profile_edit_completed');
            toast.success('Profile updated successfully!');
            navigate('/candidate/dashboard');
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const toggleSector = (sector) => {
        setFormData(prev => ({
            ...prev,
            preferred_sectors: prev.preferred_sectors.includes(sector)
                ? prev.preferred_sectors.filter(s => s !== sector)
                : [...prev.preferred_sectors, sector]
        }));
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={() => navigate('/candidate/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 md:px-8 py-10">
                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Your Profile
                </span>
                <h1 className="text-4xl font-bold mb-1 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Edit Profile</h1>
                <p className="text-slate-500 mb-8" style={{ fontFamily: 'Barlow, sans-serif' }}>Complete your profile to get better job matches</p>

                <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 border border-purple-200/50 shadow-sm space-y-6">
                    <div>
                        <Label htmlFor="skills" className="text-slate-700 font-semibold text-sm">Skills (comma-separated)</Label>
                        <Input id="skills" value={formData.skills} onChange={(e) => setFormData({...formData, skills: e.target.value})} placeholder="e.g. Microsoft Office, Communication, Data Entry" className="mt-1.5 rounded-xl border-purple-200/50" data-testid="skills-input" />
                    </div>

                    <div>
                        <Label htmlFor="experience" className="text-slate-700 font-semibold text-sm">Experience</Label>
                        <Textarea id="experience" value={formData.experience} onChange={(e) => setFormData({...formData, experience: e.target.value})} placeholder="Describe your work experience..." rows={4} className="mt-1.5 rounded-xl border-purple-200/50" data-testid="experience-input" />
                    </div>

                    <div>
                        <Label htmlFor="location" className="text-slate-700 font-semibold text-sm">Location</Label>
                        <Input id="location" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="e.g. London, UK" className="mt-1.5 rounded-xl border-purple-200/50" data-testid="location-input" />
                    </div>

                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Preferred Sectors</Label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                            {['office', 'hospitality', 'retail', 'logistics', 'trades'].map(sector => (
                                <button
                                    key={sector}
                                    type="button"
                                    onClick={() => toggleSector(sector)}
                                    className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                                        formData.preferred_sectors.includes(sector)
                                            ? 'text-white shadow-sm'
                                            : 'border border-purple-200/50 text-slate-600 hover:border-[#8B5CF6] hover:bg-purple-50'
                                    }`}
                                    style={formData.preferred_sectors.includes(sector) ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                    data-testid={`sector-btn-${sector}`}
                                >
                                    {sector.charAt(0).toUpperCase() + sector.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="availability" className="text-slate-700 font-semibold text-sm">Availability</Label>
                        <Textarea id="availability" value={formData.availability} onChange={(e) => setFormData({...formData, availability: e.target.value})} placeholder="When are you available to start?" rows={2} className="mt-1.5 rounded-xl border-purple-200/50" data-testid="availability-input" />
                    </div>

                    <Button type="submit" className="w-full btn-primary h-12" disabled={loading} data-testid="submit-btn">
                        {loading ? 'Saving...' : 'Save Profile'}
                    </Button>
                </form>
            </main>
        </div>
    );
};

export default CandidateProfile;
